import { ResponseGetAssigns } from '@vindr/services/apis/assign.api';
import { ResponseGetOrderWorklist } from '@vindr/services/apis/order.api';
import { ResponseFetchStudyList } from '@vindr/services/apis/study.api';
import { Modal, Typography } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import { ModalStaticFunctions } from 'antd/es/modal/confirm';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useImmer } from 'use-immer';
import WebcamRecorder from '../Capture-v2/WebcamRecorder';
import ReportForm, { StateReportParams } from './ReportForm';

const { Title } = Typography;

interface ReportFormProps {
  study_id?: ResponseFetchStudyList['data'][number]['study_id'] | null;
  order_id?: ResponseGetOrderWorklist['data'][number]['id'] | null;
  assign?: ResponseGetAssigns['data'][number] | null;
  onCancel: () => void;
  onMissingOrder?: () => void | null;
  visible?: boolean;
  attachmentParams?: Parameters<typeof WebcamRecorder>['0']['params'];
  modal: Omit<ModalStaticFunctions, 'warn'>;
  message: MessageInstance;
  iframe?: [
    { open: boolean; url: string },
    (study_instance_uid: string, modality?: string | string[]) => void,
    () => void,
    (study_instance_uid: string, isOpen?: boolean) => void
  ];
  motion?: boolean;
}

const Report = ({
  visible,
  study_id = null,
  order_id = null,
  assign = null,
  onCancel,
  onMissingOrder = null,
  attachmentParams,
  modal,
  iframe,
  message,
  motion = false,
}: ReportFormProps) => {
  const { t } = useTranslation('Vindoc');

  const [iframeState] = iframe || [];
  const isOpenViewer = iframeState?.open;
  const [recordVideo, setRecordVideo] = useImmer({ open: false, recording: false });

  const warningCloseCamera: StateReportParams['warningCloseCamera'] = useCallback(
    (
      callback,
      settings = {
        title: t('Are you sure want to close the camera while recording?'),
        content: t(
          'Shutting off the camera while recording will cause you to lose the video you are capturing.'
        ),
        okText: t('Close'),
        cancelText: t('Continue Recording'),
      }
    ) => {
      if (!recordVideo.recording) return callback();

      modal.confirm({
        className: 'delete-modal',
        zIndex: 10_0000,
        ...settings,
        onOk: () => {
          callback();
        },
      });
    },
    [modal, recordVideo.recording, t]
  );

  const open = useMemo(
    () => visible && !!(study_id || assign || order_id),
    [assign, order_id, study_id, visible]
  );

  return (
    <Modal
      destroyOnClose
      footer={null}
      keyboard={false}
      maskClosable={false}
      maskStyle={isOpenViewer ? { width: '50%' } : null}
      open={open}
      style={{ top: 10 }}
      title={<Title level={3}>{t('Report')}</Title>}
      wrapClassName={isOpenViewer ? 'w-1/2' : ''}
      zIndex={1001}
      className={`vindr-modal add-order max-w-screen ${
        isOpenViewer ? 'w-[49vw]' : 'w-screen md:w-[80vw]'
      }`}
      onCancel={() => warningCloseCamera(onCancel)}
      {...(!motion && { transitionName: 'none', maskTransitionName: 'none' })}
    >
      <ReportForm
        {...{
          study_id,
          order_id,
          assign,
          onMissingOrder,
          attachmentParams,
          recordVideo,
          setRecordVideo,
          warningCloseCamera,
          message,
          modal,
          iframe,
        }}
      />
    </Modal>
  );
};

export default memo(Report, (prevProps, nextProps) => {
  return (
    prevProps.visible === nextProps.visible &&
    prevProps.study_id === nextProps.study_id &&
    prevProps.assign === nextProps.assign &&
    isEqual(prevProps.attachmentParams, nextProps.attachmentParams)
  );
});
